import { useState } from "react";
import { colors, fonts, CONTACT_TYPES } from "../../styles/theme";
import { Badge } from "../../components/ui";
import { updateContact, deriveContactType, replaceTypeTag, displayName } from "../../services/api";

export default function TypeSelector({ contact, typeInfo, onContactUpdate }) {
  const [open, setOpen] = useState(false);
  const [confirmType, setConfirmType] = useState(null);
  const [saving, setSaving] = useState(false);
  const currentType = deriveContactType(contact.tags, contact.ghlTags);

  const handleSelect = (typeId) => {
    if (typeId === currentType) {
      setOpen(false);
      return;
    }
    setConfirmType(typeId);
  };

  const handleConfirm = async () => {
    if (!confirmType) return;
    setSaving(true);
    try {
      const newTags = replaceTypeTag(contact.tags, confirmType);
      const updated = await updateContact(contact.id, { email: contact.email, tags: newTags });
      if (onContactUpdate) onContactUpdate(updated);
      contact.tags = newTags;
    } catch (err) {
      alert(`Failed to change type: ${err.message}`);
    } finally {
      setSaving(false);
      setConfirmType(null);
      setOpen(false);
    }
  };

  const confirmInfo = confirmType ? CONTACT_TYPES[confirmType] : null;

  return (
    <div style={{ position: "relative", marginBottom: 16 }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "4px 12px 4px 10px",
          borderRadius: 6,
          border: `1px solid ${typeInfo.color}44`,
          background: typeInfo.bg,
          color: typeInfo.color,
          fontSize: 12,
          fontFamily: fonts.body,
          fontWeight: 700,
          cursor: "pointer",
          transition: "all 0.15s",
        }}
      >
        {typeInfo.label}
        <span style={{ fontSize: 10, opacity: 0.6 }}>
          {open ? "\u25B2" : "\u25BC"}
        </span>
      </button>

      {open && !confirmType && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            marginTop: 4,
            background: colors.bgCard,
            border: `1px solid ${colors.border}`,
            borderRadius: 8,
            boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
            zIndex: 10,
            padding: 6,
            display: "flex",
            flexWrap: "wrap",
            gap: 4,
            maxWidth: 400,
          }}
        >
          {Object.entries(CONTACT_TYPES).map(([id, info]) => (
            <button
              key={id}
              onClick={() => handleSelect(id)}
              style={{
                padding: "4px 10px",
                borderRadius: 5,
                border: `1px solid ${id === currentType ? info.color : colors.border}`,
                background: id === currentType ? info.bg : "transparent",
                color: id === currentType ? info.color : colors.textSecondary,
                fontSize: 11,
                fontFamily: fonts.body,
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.15s",
              }}
            >
              {info.label}
            </button>
          ))}
        </div>
      )}

      {confirmType && confirmInfo && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            marginTop: 4,
            background: colors.bgCard,
            border: `1px solid ${colors.border}`,
            borderRadius: 8,
            boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
            zIndex: 10,
            padding: 16,
            minWidth: 280,
          }}
        >
          <div style={{ fontSize: 13, color: colors.text, marginBottom: 12 }}>
            Change <strong>{displayName(contact)}</strong> from{" "}
            <Badge color={typeInfo.color} bg={typeInfo.bg}>{typeInfo.label}</Badge>{" "}
            to{" "}
            <Badge color={confirmInfo.color} bg={confirmInfo.bg}>{confirmInfo.label}</Badge>?
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button
              onClick={() => { setConfirmType(null); setOpen(false); }}
              disabled={saving}
              style={{
                padding: "6px 14px",
                borderRadius: 6,
                border: `1px solid ${colors.border}`,
                background: "transparent",
                color: colors.textSecondary,
                fontSize: 12,
                fontFamily: fonts.body,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={saving}
              style={{
                padding: "6px 14px",
                borderRadius: 6,
                border: "none",
                background: confirmInfo.color,
                color: "#fff",
                fontSize: 12,
                fontFamily: fonts.body,
                fontWeight: 600,
                cursor: saving ? "wait" : "pointer",
                opacity: saving ? 0.6 : 1,
              }}
            >
              {saving ? "Saving..." : "Confirm"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
